import { useState, type ReactNode } from 'react';

import { Avatar, Stack } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import AppBar from '@/karehero-app/shell/AppBar';
import Navigation from '@/karehero-app/shell/Navigation';
import AccountSheet, {
  type AccountView,
} from '@/karehero-app/access/AccountSheet';
import { type CircleView } from '@/karehero-app/shell/CircleSwitcher';

/**
 * The account the app bar carries. Passing it is what puts the bar on a screen:
 * everything after the account exists has one, everything before it does not.
 */
export type Account = {
  /** Named in the sheet, so a wrong account is visible. */
  email?: string;
  onSignOut?: () => void;
  /**
   * Their initials, once we have a name to make them out of. Before that the
   * avatar carries a person icon: the access steps and the first questions of
   * the intake happen before we have asked, and made-up initials would be a
   * worse answer than none.
   */
  initials?: string;
  /** Name beside the logo, e.g. the person being cared for. */
  title?: string;
  /**
   * What this journey is called, in place of the logo.
   *
   * The questions are somewhere you are rather than a page you are on, so the
   * bar says which set you are answering. The logo goes with it: it is a home
   * button on a screen with no home to go to yet, and leaving it there next to
   * the title made the bar look like two headers.
   */
  label?: string;
  /** Bell tap. The bell only shows when provided. */
  onNotifications?: () => void;
  /** A menu button in place of the logo. See Navigation's `onMenu`. */
  onMenu?: () => void;
  /**
   * A control beside the avatar, for a screen that can be closed.
   *
   * In the bar rather than under it: a way out on a strip of its own is a
   * second bar holding one icon, and it reads as a divider that lost its
   * contents.
   */
  action?: ReactNode;
  /** Jump-state for stories and handover minis: open on the sheet. */
  initialSheetOpen?: boolean;
  /** The fuller settings sheet. See AccountSheet's `inApp`. */
  inApp?: boolean;
  /** And which of its rows it opens on. See AccountSheet's `initialView`. */
  initialView?: AccountView;
  /** Opens Your details with one fact in its editor. */
  initialEditingFact?: string;
  /** Opens the circle drawer on one of its pages. See CircleSwitcher. */
  initialCircleView?: CircleView;
  /** And that circle with its invite sheet up. */
  initialInviteOpen?: boolean;
  /** Opens the bar's name already tapped. See CircleSwitcher. */
  initialSwitcherOpen?: boolean;
  /** Or the switch already made, so the bar names somebody else. */
  initialCircle?: string;
};

// The app bar for every screen between the account being created and Home: the
// access steps, the onboarding questions and the intake. It is the only route to
// Help and Sign out over that whole stretch, so all of them carry it.
//
// One component rather than the bar and the sheet wired up per screen. The
// avatar has to be a custom node to be tappable at all, and Navigation owns
// avatar styling precisely so it does not land as a per-instance sx at each
// call site.
const AccountBar = ({
  email,
  onSignOut,
  initials,
  title,
  label,
  onNotifications,
  onMenu,
  action,
  initialSheetOpen = false,
  inApp = false,
  initialView,
  initialEditingFact,
  initialInviteOpen,
  initialSwitcherOpen,
  initialCircleView,
  initialCircle,
}: Account) => {
  const [isSheetOpen, setIsSheetOpen] = useState(initialSheetOpen);

  // The one thing in this bar that does anything, either way it is drawn. No
  // initials until the questions have their name: made-up ones would be a guess
  // on the very screens doing the asking.
  const avatar = (
    <Avatar
      component='button'
      onClick={() => setIsSheetOpen(true)}
      aria-label='Settings'
      sx={{
        width: 36,
        height: 36,
        p: 0,
        border: 0,
        bgcolor: 'custom.neutral.400',
        color: 'custom.neutral.50',
        fontSize: 14,
        cursor: 'pointer',
      }}
    >
      {initials ?? <Icon icon='user' fontSize={16} />}
    </Avatar>
  );

  return (
    <>
      {label ? (
        // Named rather than logoed. AppBar's left align is Navigation's own
        // geometry, so the journey's bar and the app's bar are the same bar with
        // different contents: title where the logo sits, avatar where the avatar
        // sits, and the header does not jump on the way out of the questions.
        <AppBar
          title={label}
          align='left'
          size='small'
          action={
            action ? (
              <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
                {action}
                {avatar}
              </Stack>
            ) : (
              avatar
            )
          }
        />
      ) : (
        // The logo goes nowhere on purpose: before Home there is nothing behind
        // it to go to.
        <Navigation
          title={title}
          onNotifications={onNotifications}
          onMenu={onMenu}
          avatar={avatar}
          initialSwitcherOpen={initialSwitcherOpen}
          initialCircleView={initialCircleView}
          initialInviteOpen={initialInviteOpen}
          initialCircle={initialCircle}
        />
      )}
      {isSheetOpen && (
        <AccountSheet
          inApp={inApp}
          initialView={initialView}
          initialEditingFact={initialEditingFact}
          email={email}
          onSignOut={onSignOut}
          onClose={() => setIsSheetOpen(false)}
        />
      )}
    </>
  );
};

AccountBar.displayName = 'AccountBar';
export default AccountBar;
